/**
 * @file CorrectedSendDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-01
 * @modified 2026-11-01
 * @description "Send the customer the corrected …" (Cam, 30 Oct 2026 — units
 *              corrected on a delivered order whose invoice had already gone
 *              out: "such changes need to trigger the offer to users to send
 *              either a corrected invoice or a corrected statement and
 *              invoices … default to statement and invoices with clear note on
 *              statement and affected invoice").
 *
 *              Shown straight after the order save, one block per corrected
 *              invoice (the save's `corrected_send_offer` —
 *              services/correctedSendService): the correction note; where it
 *              goes by the customer's contact groups (Email Invoices → email,
 *              Postal Invoices → print; neither → choose email, print or
 *              both); and the choice — "Statement and invoices" (the default:
 *              an interim statement for the invoice's month with the note,
 *              its invoices bundled; greyed with why when the invoice is dated
 *              this month), "Invoice only" (the corrected invoice, a revised
 *              copy) or "Don't send now". Send → POST
 *              /api/invoices/:id/send-corrected; each result is said.
 *
 *              Props: { offers, onDone(text) }.
 */

import React, { useState } from 'react';
import Modal from './Modal';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import './CorrectedSendDialog.css';

const MODES = [
  { value: 'statement', label: 'Statement and invoices', hint: 'An interim statement for the month, with this note on it and the corrected invoice' },
  { value: 'invoice', label: 'Invoice only', hint: 'The corrected invoice, as a revised copy, with this note on it' },
  { value: 'none', label: 'Don\'t send now', hint: 'Nothing is sent — the note stays on the invoice for any later copy' }
];

/** Where it goes, in words. */
export function routeWords(offer) {
  const r = offer?.route || {};
  if (r.needsChoice) return `${offer?.customer?.name || 'The customer'} is in neither Email Invoices nor Postal Invoices — choose how`;
  const parts = [];
  if (r.email) parts.push(`emailed to ${offer.email_to}`);
  if (r.print) parts.push('printed');
  return `${parts.join(' and ')} (${r.why})`;
}

function Body({ offers, state, setState, busy, results }) {
  useSlideOutButton([busy, Object.keys(results).length]);
  return (
    <div className="corrected-send-body">
      {offers.map(o => {
        const st = state[o.invoice_id] || {};
        const res = results[o.invoice_id];
        return (
          <div key={o.invoice_id} className="corrected-send-invoice">
            <h4>{o.invoice_no} · {o.customer?.account_no ? `${o.customer.account_no} ` : ''}{o.customer?.name}</h4>
            {o.note && <p className="corrected-send-note">{o.note}</p>}
            <div className="corrected-send-modes" role="radiogroup" aria-label={`What to send for ${o.invoice_no}`}>
              {MODES.map(m => {
                const blocked = m.value === 'statement' ? o.statement_block : null;
                const on = (st.mode || o.default_mode) === m.value;
                return (
                  <button key={m.value} type="button" role="radio" aria-checked={on}
                    className={`corrected-send-mode${on ? ' on' : ''}`} disabled={!!blocked || busy || !!res}
                    title={blocked || m.hint}
                    onClick={() => setState(s => ({ ...s, [o.invoice_id]: { ...st, mode: m.value } }))}>
                    <span className="material-symbols-rounded" aria-hidden="true">{on ? 'radio_button_checked' : 'radio_button_unchecked'}</span>
                    {m.label}{m.value === 'statement' && !blocked ? ' (recommended)' : ''}
                  </button>
                );
              })}
            </div>
            {o.statement_block && <p className="corrected-send-hint">Statement and invoices: {o.statement_block}.</p>}
            {(st.mode || o.default_mode) !== 'none' && (
              <p className="corrected-send-route">
                {o.route?.needsChoice ? (
                  <>
                    {routeWords(o)}:{' '}
                    {['email', 'print', 'both'].map(c => (
                      <button key={c} type="button" className={`corrected-send-choice${st.choice === c ? ' on' : ''}`}
                        disabled={busy || !!res || (c !== 'print' && !o.email_to)}
                        title={c !== 'print' && !o.email_to ? 'No email address for this customer' : undefined}
                        onClick={() => setState(s => ({ ...s, [o.invoice_id]: { ...st, choice: c } }))}>
                        {c === 'both' ? 'Email and print' : c === 'email' ? 'Email' : 'Print'}
                      </button>
                    ))}
                  </>
                ) : <>It will be {routeWords(o)}.</>}
              </p>
            )}
            {res && <p className={`corrected-send-result${res.ok ? '' : ' err'}`}>{res.text}</p>}
          </div>
        );
      })}
    </div>
  );
}

function CorrectedSendDialog({ offers, onDone }) {
  const [state, setState] = useState({});
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState({});
  const finished = offers.every(o => results[o.invoice_id]);
  const missingChoice = offers.some(o => {
    const st = state[o.invoice_id] || {};
    return !results[o.invoice_id] && (st.mode || o.default_mode) !== 'none' && o.route?.needsChoice && !st.choice;
  });

  const send = async () => {
    setBusy(true);
    const next = { ...results };
    for (const o of offers) {
      if (next[o.invoice_id]) continue;
      const st = state[o.invoice_id] || {};
      try {
        const res = await fetch(`${API_URL}/api/invoices/${o.invoice_id}/send-corrected`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ mode: st.mode || o.default_mode, choice: st.choice || null, note: o.note || null })
        });
        const body = await res.json().catch(() => ({}));
        next[o.invoice_id] = res.ok && body.success
          ? { ok: !(body.results || []).some(r => !r.ok), text: body.message }
          : { ok: false, text: body.error || 'Not sent' };
      } catch (err) {
        next[o.invoice_id] = { ok: false, text: err.message };
      }
      setResults({ ...next });
    }
    setBusy(false);
  };

  const close = () => onDone(Object.values(results).map(r => r.text).join(' ') || null);
  return (
    <Modal isOpen onClose={busy ? () => {} : close} title="Send the customer the corrected copy" size="lg" className="corrected-send"
      footer={(
        <div className="buttons slideout">
          <div>
            <button type="button" className="btn-slideout" onClick={close} disabled={busy}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">{finished ? 'Close' : 'Not now'}</span>
            </button>
          </div>
          {!finished && (
            <div>
              <button type="button" className="btn-slideout btn-slideout-primary" onClick={send} disabled={busy || missingChoice}
                title={missingChoice ? 'Choose email, print or both for the customer in neither group' : undefined}>
                <div className="icon-wrapper"><span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>{busy ? 'progress_activity' : 'send'}</span></div>
                <span className="btn-label">{busy ? 'Sending…' : 'Send'}</span>
              </button>
            </div>
          )}
        </div>
      )}>
      <Body offers={offers} state={state} setState={setState} busy={busy} results={results} />
    </Modal>
  );
}

export default CorrectedSendDialog;
